import { getChronoOrder } from '@/utils/periodOrder';

// A periódusok "startat" (kezdő időpont, másodpercben) mezőjének KÖZÖS,
// egyetlen igazság-forrásként szolgáló kiszámítása.
//
// MIÉRT KELL EZ A FÁJL: korábban ugyanez a számítás HÁROM külön helyen élt,
// egymástól eltérő logikával (dbmanager.getinitData(), dbmanager.gamedata(),
// game.js SET_PERIOD). Mivel a `loadGameData()` (belépés, reconnect ÉS a 10
// másodperces periodikus resync) újraszámolta a `gamedata()`-beli verzióval,
// a `SET_PERIOD`-ban beállított érték max. 10 másodpercen belül visszaíródott
// a másik logika eredményére - az óra tehát periódusváltás után "visszaugrott".
// Ezért mostantól mindhárom hívási hely EZT a függvényt használja.
//
// A SZÁMÍTÁS SZABÁLYA (l. az alábbi `maxReachedIndex`-et):
// egy periódus időtartama csak akkor számít bele a rákövetkező periódusok
// kezdő idejébe, ha a mérkőzés ténylegesen eljutott odáig. Enélkül egy rendes
// játékidőben eldőlt mérkőzésnél a "Mérkőzés vége" tévesen 80:00-t mutatna
// (a le nem játszott hosszabbításokkal együtt) a helyes 60:00 helyett.
//
// A KORÁBBI IMPLEMENTÁCIÓ HIBÁJA: az azt nézte, hogy MINDEN EGYES periódushoz
// tartozik-e rögzített esemény. Ha egy periódusváltás rögzítése kimaradt (pl.
// a jegyzőkönyvvezető nem sorrendben lépkedett a periódusok között), az adott
// periódus tévesen "le nem játszottnak" számított, és az utána következő
// periódusok kezdő ideje elcsúszott (a 7m dobások 80:00 helyett 75:00-ra).
// Elég viszont csak a LEGMESSZEBBI elért periódust ismerni: a periódusok
// időben egymás után, kihagyás nélkül következnek, tehát ha a mérkőzés elért
// a 11-es periódusig, akkor a 9-es is le lett játszva - függetlenül attól,
// hogy maradt-e róla rögzített esemény.

// A "Mérkőzés vége" periódus. Ez az EGYETLEN periódus, ami a láncot megkerülve,
// bármelyik pontról közvetlenül elérhető (a mérkőzés eldőlhet a rendes
// játékidőben is), ezért NEM jelzi, hogy a köztes hosszabbítások lejátszódtak -
// így a "meddig jutott el a meccs" számításból ki kell hagyni.
const MATCH_END_PERIOD = '13';

function sortPeriodsChronologically(periods) {
    return [...periods].sort((a, b) => {
        // A szerver saját, hiteles "order" mezője az elsődleges; a hardcode-olt
        // CHRONO_MAP csak tartalék, ha az "order" hiányzik vagy nem szám.
        const orderA = parseInt(a.order);
        const orderB = parseInt(b.order);
        if (!isNaN(orderA) && !isNaN(orderB)) {
            return orderA - orderB;
        }
        return getChronoOrder(a.value) - getChronoOrder(b.value);
    });
}

/**
 * Kiszámolja és BEÁLLÍTJA minden periódus `startat` mezőjét.
 * A `periods` tömb elemeit helyben módosítja (ugyanazokat az objektumokat,
 * amikre a `window.gamedata.periods` és a `state.gamedata.periods` is mutat).
 *
 * @param {Array}  periods          a periódus-objektumok (`value`, `duration`, `order`, `type`)
 * @param {Set|Array} reachedValues azon periódus-értékek, amikhez tartozik rögzített esemény
 * @param {String} currentPeriod    a jelenleg kiválasztott periódus értéke
 */
export function applyPeriodStartAt(periods, reachedValues, currentPeriod) {
    if (!Array.isArray(periods) || periods.length === 0) return periods;

    const reached = reachedValues instanceof Set
        ? reachedValues
        : new Set((reachedValues || []).map(String));

    const sorted = sortPeriodsChronologically(periods);

    // Meddig jutott el ténylegesen a mérkőzés? A kiválasztott periódust is
    // ide számítjuk: ha a jegyzőkönyvvezető átvált a 7m dobásokra, az órának
    // azonnal a helyes (80:00) időre kell állnia, még mielőtt ott bármilyen
    // esemény rögzülne.
    let maxReachedIndex = 0;
    sorted.forEach((p, index) => {
        const value = String(p.value);
        if (value === MATCH_END_PERIOD) return;
        if (reached.has(value) || value === String(currentPeriod)) {
            maxReachedIndex = index;
        }
    });

    let accumulated = 0;
    sorted.forEach((p, index) => {
        p.startat = accumulated;
        // Csak az elért periódusok (bezárólag a legmesszebbi) hossza adódik
        // hozzá a láncohoz - az azon túliak 0-val számítanak.
        if (index <= maxReachedIndex) {
            accumulated += (parseInt(p.duration) || 0);
        }
    });

    return periods;
}

/**
 * Összegyűjti egy nyers rekord-lista alapján, mely periódusokban van esemény.
 * Több lista is átadható (fő idővonal, DMR idővonal, teljes DB-dump stb.).
 */
export function collectReachedPeriods(...lists) {
    const reached = new Set(['1']);
    lists.forEach(list => {
        (list || []).forEach(item => {
            if (item && item.period !== undefined && item.period !== null) {
                reached.add(String(item.period));
            }
        });
    });
    return reached;
}
